import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import heroImage from "../assets/images/hero.jpg";

const StyledHeroSection = styled(Box)(({ theme }) => ({
  minHeight: "82vh",
  width: "100%",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: `url(${heroImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundColor: "#000",
  padding: theme.spacing(12, 2, 8),
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    minHeight: "86vh",
    padding: theme.spacing(13, 3, 9),
  },
  [theme.breakpoints.up("md")]: {
    minHeight: "88vh",
    padding: theme.spacing(14, 4, 10),
  },
}));

const StyledOverlay = styled(Box)({
  position: "absolute",
  inset: 0,
  background: "linear-gradient(180deg, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.48) 100%)",
});

const StyledContent = styled(Box)(({ theme }) => ({
  position: "relative",
  zIndex: 1,
  textAlign: "center",
  color: "white",
  maxWidth: "880px",
  width: "100%",
  margin: "0 auto",
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  fontSize: "2.35rem",
  fontWeight: 800,
  lineHeight: 1.08,
  textShadow: "0 3px 12px rgba(0,0,0,0.55)",
  [theme.breakpoints.up("sm")]: {
    fontSize: "3.2rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "4rem",
  },
}));

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  fontSize: "1.25rem",
  fontWeight: 600,
  lineHeight: 1.3,
  textShadow: "0 2px 8px rgba(0,0,0,0.55)",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.65rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "2rem",
  },
}));

const StyledIntro = styled(Typography)(({ theme }) => ({
  maxWidth: 720,
  margin: "0 auto",
  lineHeight: 1.75,
  fontSize: "1rem",
  opacity: 0.97,
  textShadow: "0 2px 8px rgba(0,0,0,0.6)",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.15rem",
  },
}));

const StyledTagline = styled(Typography)(({ theme }) => ({
  maxWidth: 720,
  margin: theme.spacing(2.5, "auto", 3.5),
  fontSize: "1.05rem",
  lineHeight: 1.6,
  fontWeight: 600,
  color: theme.palette.primary.light,
  textShadow: "0 2px 8px rgba(0,0,0,0.65)",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.2rem",
  },
}));

const StyledHeroButton = styled(Button)(({ theme }) => ({
  fontSize: "1rem",
  padding: theme.spacing(1.15, 4),
  minWidth: 220,
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.05rem",
  },
}));

interface HeroSectionProps {
  onOpenBookingDialog: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onOpenBookingDialog }) => {
  return (
    <StyledHeroSection id="home" component="section" aria-label="Welkom bij Pippaya">
      <StyledOverlay aria-hidden="true" />
      <StyledContent>
        <StyledTitle variant="h1" component="h1">
          Pippaya Hondenopvang
        </StyledTitle>
        <StyledSubtitle variant="h2" component="h2">
          Waar logeren ook een beetje vakantie is
        </StyledSubtitle>

        <StyledIntro variant="body1">
          Bij Pippaya komt een hond niet zomaar naar de opvang, maar eigenlijk gewoon een beetje bij ons thuis. Kleinschalig, persoonlijk en met alle ruimte om te wandelen, spelen, snuffelen én lekker bij te komen.
        </StyledIntro>

        <StyledTagline variant="body1">
          Welkom bij Pippaya — waar jouw hond misschien stiekem wel de leukste vakantie heeft. 💗🐾
        </StyledTagline>

        <StyledHeroButton
          variant="contained"
          color="secondary"
          size="large"
          onClick={onOpenBookingDialog}
          aria-label="Neem contact op met Pippaya"
        >
          Neem contact op
        </StyledHeroButton>
      </StyledContent>
    </StyledHeroSection>
  );
};
